import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, PlayIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/outline';

const FuturesTradingPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(0);

  const features = [
    {
      icon: "✓",
      title: "Instantaneous trading access",
      description: "Gain immediate access to the market with deep liquidity and tight spreads on all major crypto futures."
    },
    {
      icon: "⚡",
      title: "Hedging capabilities",
      description: "Protect your portfolio by offsetting price movements with long and short positions."
    },
    {
      icon: "🎯",
      title: "Enter and exit positions instantly",
      description: "Get your positions filled and executed instantly, giving you a clear overview of your trades at all times."
    },
    {
      icon: "📊",
      title: "Refine your analysis in demo trading",
      description: "Improve your trading skills and experiment with new strategies in our demo trading environment, risk-free."
    },
    {
      icon: "🔄",
      title: "Non-stop crypto trading",
      description: "Trade crypto futures 24/7, allowing you to react to market movements at any time, day or night."
    }
  ];

  const walletFeatures = [
    {
      icon: "💼",
      title: "Cross-margin",
      description: "Utilize the entire balance of your Futures Wallet as collateral for all your open positions."
    },
    {
      icon: "🛡️",
      title: "Superior risk management",
      description: "Manage your risk exposure with real-time margin monitoring and automated liquidation protocols."
    }
  ];

  const ecosystemFeatures = [
    {
      icon: "🔄",
      title: "Exchange",
      description: "Swap over 500 market pairs instantly."
    },
    {
      icon: "💰",
      title: "Earn interest",
      description: "Earn up to 16% APR on your crypto."
    },
    {
      icon: "💳",
      title: "Credit lines",
      description: "Borrow cash or stablecoins instantly."
    }
  ];

  const faqs = [
    {
      question: "What is futures trading?",
      answer: "Futures trading allows you to speculate on the future price of cryptocurrencies without owning the underlying asset. You can profit from both rising and falling markets."
    },
    {
      question: "How do futures trading apps work?",
      answer: "Our futures trading platform provides real-time market data, advanced charting tools, and instant execution capabilities to help you make informed trading decisions."
    },
    {
      question: "What is leverage?",
      answer: "Leverage allows you to control a larger position with a smaller amount of capital. While it can amplify profits, it also increases risk, so careful risk management is essential."
    },
    {
      question: "What is margin trading?",
      answer: "Margin trading enables you to borrow funds to increase your trading position. You'll need to maintain sufficient collateral to keep your positions open."
    },
    {
      question: "How do I trade crypto futures?",
      answer: "Start by creating an account, funding your wallet, and then you can begin trading futures contracts with our intuitive platform interface."
    }
  ];

  const steps = [
    "Create an account",
    "Fund your wallet", 
    "Start trading"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Advance your strategy with crypto futures
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl">
                Capitalize on the ups and downs of over 100 popular contracts with our advanced trading platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-all duration-300 flex items-center gap-2 group">
                  Sign Up
                  <ChevronRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="relative z-10">
                <img 
                  src={require('../assets/trading/futures.webp')} 
                  alt="Futures Trading Interface" 
                  className="w-full max-w-md mx-auto rounded-2xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-primary-400/20 to-primary-600/20 rounded-2xl"></div>
              </div>
              <div className="absolute -bottom-6 -right-6 w-72 h-72 bg-gradient-to-br from-primary-300 to-primary-600 rounded-full opacity-20 blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Power your strategy with these advantages
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wallet Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Take control of your positions from a dedicated wallet
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {walletFeatures.map((feature, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-2xl border border-gray-200">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learn More Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Everything about Futures trading on Segway Capital
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Learn more about the benefits and risks of trading crypto futures.
          </p>
          <Link 
            to="/learn/futures" 
            className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700 transition-colors duration-300"
          >
            Read more
            <ChevronRightIcon className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* How to Start Trading */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                How to start trading?
              </h2>
              <div className="space-y-4">
                {steps.map((step, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center font-semibold">
                      {index + 1}
                    </div>
                    <span className="text-lg text-gray-700">{step}</span>
                  </div>
                ))}
              </div>
              <button className="bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-all duration-300">
                Sign Up
              </button>
            </div>
            <div className="relative">
                                   <img
                       src={require('../assets/trading/advanced-analysis.webp')}
                       alt="Advanced Trading Analysis"
                       className="w-full max-w-md mx-auto rounded-2xl shadow-2xl"
                     />
              <div className="absolute inset-0 bg-gradient-to-br from-primary-400/20 to-primary-600/20 rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Frequently asked questions
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <button
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-300"
                  onClick={() => setExpandedFaq(expandedFaq === index ? -1 : index)}
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  {expandedFaq === index ? (
                    <MinusIcon className="w-5 h-5 text-primary-600" />
                  ) : (
                    <PlusIcon className="w-5 h-5 text-primary-600" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Driving Wealth Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-primary-100/50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Driving the next generation of wealth
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Segway Capital is a leading regulated institution offering a comprehensive suite of financial services for digital assets and traditional investments.
              </p>
              <button className="bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-all duration-300 flex items-center gap-2 group">
                Sign up
                <PlusIcon className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
              </button>
            </div>
            <div className="relative">
              <img 
                src={require('../assets/traders-profit-354772.jpg')} 
                alt="Successful Trading Team" 
                className="w-full max-w-md mx-auto rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-primary-400/20 to-primary-600/20 rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Ecosystem Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Utilize the entire Segway Capital ecosystem to grow your wealth
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {ecosystemFeatures.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 text-center">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to start your futures trading journey?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join thousands of traders who trust Segway Capital for their crypto futures trading needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-all duration-300">
              Get Started Now
            </button>
            <button className="border border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-all duration-300">
              Learn More
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FuturesTradingPage; 